import { HEADER_REQUEST_META } from '@blufin/types';

import type { HttpRequestMetadata, IHttpRequestMetadata } from './helpers/HttpRequestMetadata';
import type { HttpResponseMetadata, IHttpResponseMetadata } from './helpers/HttpResponseMetadata';
import { Base64 } from '../Base64/Base64';

export interface IRequestMetadataCodec {
  decodeRequest: (headers: Record<string, string | undefined>) => IHttpRequestMetadata | undefined;
  decodeResponse: (headers: Record<string, string | undefined>) => IHttpResponseMetadata | undefined;
  encodeRequest: (meta: HttpRequestMetadata) => string;
  encodeResponse: (meta: HttpResponseMetadata) => string;
}

/**
 * Codec for encoding and decoding request/response metadata transmitted via HTTP headers.
 * Handles bidirectional metadata transport between client and server using base64-encoded JSON.
 */
export const RequestMetadataCodec: IRequestMetadataCodec = {
  /**
   * Parse request metadata information from request headers (client to server).
   * @param headers - The request headers.
   */
  decodeRequest: (headers: Record<string, string | undefined>): IHttpRequestMetadata | undefined => {
    const requestMetaHeaderBase64Encoded: string | undefined =
      (headers || {})[HEADER_REQUEST_META] || (headers || {})[HEADER_REQUEST_META.toLowerCase()];
    if (!requestMetaHeaderBase64Encoded) return undefined;
    try {
      const decodedHeader: string = Base64.decode(requestMetaHeaderBase64Encoded);
      const parsed: IHttpRequestMetadata = JSON.parse(decodedHeader);
      return parsed;
    } catch (err: unknown) {
      throw new Error(`Failed to parse request meta header: ${err instanceof Error ? err.message : String(err)}`);
    }
  },
  /**
   * Parse response metadata information from response headers (server to client).
   * @param headers - The response headers.
   */
  decodeResponse: (headers: Record<string, string | undefined>): IHttpResponseMetadata | undefined => {
    const requestMetaHeaderBase64Encoded: string | undefined =
      (headers || {})[HEADER_REQUEST_META] || (headers || {})[HEADER_REQUEST_META.toLowerCase()];
    if (!requestMetaHeaderBase64Encoded) return undefined;
    try {
      const decodedHeader = Base64.decode(requestMetaHeaderBase64Encoded);
      const parsed: IHttpResponseMetadata = JSON.parse(decodedHeader);
      return parsed;
    } catch (err: unknown) {
      throw new Error(`Failed to parse response meta header: ${err instanceof Error ? err.message : String(err)}`);
    }
  },
  /**
   * Encode request metadata sent from client to server, via HTTP header.
   * @param meta - The data to encode and send.
   */
  encodeRequest(meta: HttpRequestMetadata): string {
    const jsonString = JSON.stringify(meta);
    return Base64.encode(jsonString);
  },
  /**
   * Encode response metadata sent from server to client via HTTP header.
   * @param meta - The response metadata to encode and send.
   */
  encodeResponse(meta: HttpResponseMetadata): string {
    const jsonString = JSON.stringify(meta);
    return Base64.encode(jsonString);
  }
};
